import { createRepairEngine, formatAnalyzed } from '@jsonfmt/core';

import { createAnalysisCache, type AnalysisCache } from './analysisCache';
import { isWorkerRequest, type WorkerRequest, type WorkerResponse } from './protocol';

/**
 * The analyzer Worker (ADR-0010).
 *
 * IT POSTS BACK `report`, NEVER `AnalyzedDocument`. That is not a style preference, it is
 * the difference between a Worker that helps and one that hurts, twice over:
 *
 *   - `AnalyzedDocument.lines` is a `LineIndex`, an object with METHODS. `structuredClone`
 *     throws `DataCloneError` on it. Posting one back does not degrade, it fails.
 *     `analyzer.contract.test.ts` asserts exactly that, so the claim is checked rather than
 *     believed.
 *   - even if it were clonable, `tokens` is hundreds of thousands of objects on a 2 MB
 *     document, and cloning them costs more than the analysis did. `LintReport` is a few
 *     hundred plain objects and survives `structuredClone` — property-tested in core.
 *
 * The module is split in two halves on purpose:
 *
 *   `handleRequest(request, cache)`  pure, synchronous, exported, and the ONLY place the
 *                                    three operations are implemented.
 *   the `addEventListener` at the bottom  ten lines of transport, guarded so that importing
 *                                    this module from a test (or from the main thread's
 *                                    inline transport) attaches nothing to `window`.
 *
 * `../analyzer.ts` calls `handleRequest` directly for documents below the Worker threshold.
 * One implementation, two transports — which is what makes the contract test meaningful
 * instead of ceremonial.
 */

/** `Partial<FormatOptions>` etc. arrive on the request; each branch takes its own. */
export function handleRequest(request: WorkerRequest, cache: AnalysisCache): WorkerResponse {
  const { seq, version, source } = request;

  try {
    switch (request.kind) {
      case 'lint': {
        const doc = cache.analyze(version, source, request.options);
        return { kind: 'lint', seq, version, report: doc.report };
      }

      case 'format': {
        // Lint options are not format options: the analysis behind a format is the default
        // pipeline, and `request.options` tunes only the printer.
        const doc = cache.analyze(version, source);
        return { kind: 'format', seq, version, result: formatAnalyzed(doc, request.options) };
      }

      case 'repair': {
        // The engine re-analyses after every pass. Handing it the cache's bound `analyze`
        // means pass 0 — the analysis of the text the caller just linted — is free.
        const engine = createRepairEngine({ analyze: cache.boundTo(version) });
        return { kind: 'repair', seq, version, result: engine.repair(source, request.options) };
      }
    }
  } catch (cause) {
    // The engine is documented not to throw, and 2413 core tests say it does not. This is
    // the net under that claim: a throw here must reach the caller as a rejected promise,
    // not as a request that never answers and a spinner that never stops.
    return {
      kind: 'error',
      seq,
      version,
      name: nameOf(cause),
      // NEVER `String(cause)` of something that may embed the document. Only the engine's
      // own message, which is authored by us and carries no user text.
      message: messageOf(cause),
    };
  }
}

function nameOf(cause: unknown): string {
  const name = (cause as { name?: unknown } | null)?.name;
  return typeof name === 'string' && name.length > 0 ? name : 'Error';
}

function messageOf(cause: unknown): string {
  const message = (cause as { message?: unknown } | null)?.message;
  return typeof message === 'string' ? message : 'analysis failed';
}

/** The two members of the worker global this file touches. */
interface WorkerScope {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  postMessage(message: unknown): void;
}

/**
 * True only inside a real dedicated Worker.
 *
 * `typeof self !== 'undefined'` is NOT enough — in a browser window `self === window`, so a
 * test or the inline transport importing this module would install a `message` listener on
 * the page and answer `postMessage` calls meant for someone else. `WorkerGlobalScope` exists
 * only in a worker, and `document` exists only in a window; requiring both to agree makes
 * the check unambiguous.
 */
function inDedicatedWorker(): boolean {
  const scope = globalThis as {
    WorkerGlobalScope?: unknown;
    document?: unknown;
    postMessage?: unknown;
  };
  return (
    typeof scope.WorkerGlobalScope === 'function' &&
    scope.document === undefined &&
    typeof scope.postMessage === 'function'
  );
}

if (inDedicatedWorker()) {
  const scope = globalThis as unknown as WorkerScope;
  const cache = createAnalysisCache();

  scope.addEventListener('message', (event: MessageEvent) => {
    const request: unknown = event.data;
    // Anything that is not a request we recognise is ignored rather than answered: a page
    // sharing this worker with an extension, or a future protocol variant, must not make it
    // throw inside an event handler where nobody can catch it.
    if (!isWorkerRequest(request)) return;
    scope.postMessage(handleRequest(request, cache));
  });
}
